import { AxiosError } from "axios";
import { fetchWithAuth } from "../config/axios";
import extensionLocalStorage from "./extension-local-storage";

export interface ExtensionUser {
    email: string;
    token: string;
    refreshToken?: string;
    tokenExpirationTime?: number;
}

export interface Organization {
    id: string;
    name: string;
    pms: string;
    url: string;
    users: Array<{ email: string }>;
    portal_billing_enabled: boolean;
}

export interface CustomerURL {
    id: string;
    customer_id: string;
    platform: string;
    url: string;
}

export interface Contact {
    id: string;
    customer_id: string;
    source_id: string;
    name: string;
    title: string;
    role: string;
    contact_reason: string;
    email: string;
    phone: string;
    mobile_phone: string;
    url: string;
    website: string;
    owner_id: string;
    owner_name: string;
    owner_email: string;
    mailing_street: string;
    mailing_city: string;
    mailing_state: string;
    mailing_postal_code: string;
    mailing_country: string;
    invitations: any;
    updated_at: string;
    created_at: string;
    portal_status: "uninvited" | "invited" | "active" | "inactive";
}

export interface Customer {
    id: string;
    name: string;
    type: string;
    platform: string;
    urls: CustomerURL[];
    contacts: Contact[];
    jira_items: any;
    jira_ticket: any;
    stage_name: string;
    team: string;
    services: string;
    stage_transition_date: string;
    mrr: number;
    close_date: string;
    slack_channel: string;
    record_type: string;
    unit_count: string;
    internal_information: any;
    customer_settings: any;
    customer_communication_preferences: any;
    key_dates: any;
    management_fees: any;
    funnel_stage: any;
    source_id: string;
    notice_of_termination_date: string;
    stripe_id: string;
    asset_class: string;
    accounting_manager: string;
    customer_validations: any;
    customer_services: any;
    enable_dashboard: boolean;
    br_enabled: boolean;
    fs_enabled: boolean;
    to_be_deleted: boolean;
    volumes_enabled: boolean;
    ap_enabled: boolean;
    props?: Record<string, any>;
}

/**
 * This method throws an error if the token is not exchangable
 */
export async function getEmailForToken(token: string): Promise<string> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/validate");
    url.searchParams.append("token", token);

    const response = await fetch(url.toString());
    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error?.userMessage ?? data.error?.message);
    }

    return data.email as string;
}

export async function setPasswordForUser(credentials: {
    token: string;
    password: string;
}): Promise<void> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/activate");

    const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            token: credentials.token,
            password: credentials.password,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Activation failed (${response.status}): ${errorText}`);
    }

    const payload = await response.json();

    if (payload.status !== "ok") {
        const errorMessage = payload.message || payload.error || "Unknown activation error";
        throw new Error(`Activation failed: ${errorMessage}`);
    }
}

export async function activateWithGoogle(token: string, firebaseEmail: string): Promise<void> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/activate");

    const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            token,
            firebaseEmail,
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Activation failed (${response.status}): ${errorText}`);
    }

    const payload = await response.json();

    if (payload.status !== "ok") {
        const errorMessage = payload.message || payload.error || "Unknown activation error";
        throw new Error(`Activation failed: ${errorMessage}`);
    }
}

export async function checkLoginStatus(
    email: string,
): Promise<{ allowed: boolean; message?: string }> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/check-login");

    const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
    });

    if (!response.ok) {
        throw new Error(`Login check failed (${response.status})`);
    }

    return await response.json();
}

export async function cleanupUnauthorizedUser(idToken: string): Promise<void> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/cleanup-unauthorized");

    const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
    });

    if (!response.ok) {
        throw new Error(`Cleanup failed (${response.status})`);
    }
}

export async function getExtensionUser(): Promise<ExtensionUser | null> {
    const extensionUser = JSON.parse((await extensionLocalStorage("puser")) ?? "null");

    if (!extensionUser || !extensionUser.email || !extensionUser.token) {
        return null;
    }
    return extensionUser;
}

export interface AccountingLead {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    name?: string;
    photo?: string;
    title?: string;
    department?: string;
}

export async function getOrganization(): Promise<Customer> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/org").toString();
    const response = await fetchWithAuth<Customer>(url.toString());

    return response.data;
}

export async function getAccountingLead(): Promise<AccountingLead> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/org/accounting-lead").toString();
    const response = await fetchWithAuth<AccountingLead>(url);

    return response.data;
}

export async function updatePlatform(platform: string): Promise<void> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/org/platform").toString();
    await fetchWithAuth(url, {
        method: "PATCH",
        data: { platform },
    });
}

export async function getPmsOptions(): Promise<string[]> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/org/pms-options").toString();
    const response = await fetchWithAuth<string[]>(url);

    return response.data;
}

export async function removeUser(email: string): Promise<Organization> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/organization/team");

    const response = await fetchWithAuth<Organization>(url.toString(), {
        method: "DELETE",
        data: {
            email,
        },
    });

    return response.data;
}

export async function inviteUserToOrganization(email: string): Promise<Organization> {
    if (!isValidEmail(email)) {
        throw new Error("Invalid email.");
    }

    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/organization/team");

    try {
        const response = await fetchWithAuth<Organization>(url.toString(), {
            method: "POST",
            data: {
                email,
            },
        });

        return response.data;
    } catch (error) {
        if (error instanceof AxiosError) {
            throw new Error(
                error.response?.data?.message ?? "Error inviting user to organization.",
            );
        }

        throw error;
    }
}

export async function sendFeedback(data: {
    type: "support" | "feedback";
    message: string;
}): Promise<boolean> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/feedback");

    try {
        await fetchWithAuth(url.toString(), {
            method: "POST",
            data,
        });
    } catch (error) {
        if (error instanceof AxiosError) {
            throw new Error(error.response?.data?.message ?? "Error sending feedback.");
        }

        throw error;
    }

    return true;
}

export async function notifyPasswordReset(params: {
    targetEmail: string;
    actorEmail?: string;
    ip?: string;
    userAgent?: string;
}): Promise<void> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/notify/password-reset");

    try {
        await fetch(url.toString(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(params),
        });
    } catch (_e) {
        // best-effort; ignore errors
    }
}

function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Contact management functions using new Choripan portal APIs
 */
export async function inviteContact(
    contactId: string,
): Promise<{ invitationLink: string; expiresAt: string }> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL + `/user/portal/customer-contacts/${contactId}/invite`,
    );

    const response = await fetchWithAuth<{ invitationLink: string; expiresAt: string }>(
        url.toString(),
        {
            method: "POST",
        },
    );

    return response.data;
}

export async function revokeInvitation(contactId: string): Promise<{ success: boolean }> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL + `/user/portal/customer-contacts/${contactId}/invite`,
    );

    const response = await fetchWithAuth<{ success: boolean }>(url.toString(), {
        method: "DELETE",
    });

    return response.data;
}

export async function deactivateUser(contactId: string): Promise<{ success: boolean }> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL +
            `/user/portal/customer-contacts/${contactId}/deactivate`,
    );

    const response = await fetchWithAuth<{ success: boolean }>(url.toString(), {
        method: "POST",
    });

    return response.data;
}

export async function reactivateUser(contactId: string): Promise<{ success: boolean }> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL +
            `/user/portal/customer-contacts/${contactId}/reactivate`,
    );

    const response = await fetchWithAuth<{ success: boolean }>(url.toString(), {
        method: "POST",
    });

    return response.data;
}

export async function deleteContact(contactId: string): Promise<void> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL + `/user/portal/customer-contacts/${contactId}`,
    );

    await fetchWithAuth(url.toString(), {
        method: "DELETE",
    });
}

export async function createContact(email: string): Promise<Contact> {
    const url = new URL(import.meta.env.VITE_CHORIPAN_URL + "/user/portal/customer-contacts");

    const response = await fetchWithAuth<Contact>(url.toString(), {
        method: "POST",
        data: {
            name: email.split("@")[0],
            email,
        },
    });

    return response.data;
}

export async function updateContact(
    contactId: string,
    payload: { name?: string; email?: string; propertiesIds?: string[] },
): Promise<Contact> {
    const url = new URL(
        import.meta.env.VITE_CHORIPAN_URL + `/user/portal/customer-contacts/${contactId}`,
    );

    const response = await fetchWithAuth<Contact>(url.toString(), {
        method: "PUT",
        data: payload,
    });

    return response.data;
}
